import { useRef, useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import type { Editor as TinyMCEEditor } from 'tinymce';
import { Button } from '../ui';

interface HtmlEditorProps {
  html: string;
  onSave: (html: string) => Promise<void>;
  onClose?: () => void;
  isLoading?: boolean;
  readOnly?: boolean;
}

export function HtmlEditor({ html, onSave, onClose, isLoading, readOnly }: HtmlEditorProps) {
  const editorRef = useRef<TinyMCEEditor | null>(null);
  // Store the initial content after TinyMCE normalizes it
  const initialContentRef = useRef<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const handleSave = async () => {
    if (!editorRef.current) return;

    setIsSaving(true);
    try {
      const content = editorRef.current.getContent();
      await onSave(content);
      // Update the baseline after saving
      initialContentRef.current = content;
      setHasChanges(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditorChange = (content: string) => {
    // Only mark as changed if content differs from the initial normalized content
    if (initialContentRef.current !== null) {
      setHasChanges(content !== initialContentRef.current);
    }
  };

  const handleEditorInit = (_evt: unknown, editor: TinyMCEEditor) => {
    editorRef.current = editor;
    // Capture the content after TinyMCE has normalized it
    initialContentRef.current = editor.getContent();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex justify-between items-center px-4 py-2 bg-gray-50 dark:bg-dark-recessed border-b border-gray-200 dark:border-dark-border">
        <div className="flex items-center gap-2">
          {hasChanges && (
            <span className="text-sm text-amber-600 dark:text-amber-400">Unsaved changes</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={isSaving || !hasChanges || readOnly}
            isLoading={isSaving}
          >
            Save Changes
          </Button>
          {onClose && (
            <Button variant="secondary" size="sm" onClick={onClose}>
              Close
            </Button>
          )}
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-full bg-gray-100 dark:bg-dark-recessed">
            <div className="text-center">
              <span className="inline-block w-8 h-8 border-2 border-purple-400 border-t-transparent rounded-full animate-spin mb-2" />
              <p className="text-gray-500 dark:text-gray-400">Loading editor...</p>
            </div>
          </div>
        ) : (
          <Editor
            tinymceScriptSrc={`https://cdn.tiny.cloud/1/${import.meta.env.VITE_TINYMCE_API_KEY}/tinymce/6/tinymce.min.js`}
            onInit={handleEditorInit}
            initialValue={html}
            onEditorChange={handleEditorChange}
            disabled={readOnly}
            init={{
              height: '100%',
              menubar: true,
              plugins: [
                'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
                'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                'insertdatetime', 'media', 'table', 'preview', 'help', 'wordcount'
              ],
              toolbar: 'undo redo | blocks | ' +
                'bold italic forecolor backcolor | alignleft aligncenter ' +
                'alignright alignjustify | bullist numlist outdent indent | ' +
                'removeformat | code | help',
              content_style: 'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; font-size: 14px; }',
              // Preserve all HTML structure
              verify_html: false,
              cleanup: false,
              convert_urls: false,
              valid_elements: '*[*]',
              extended_valid_elements: '*[*]',
              // Allow full document editing
              valid_children: '+body[style]',
              // Keep styles intact
              content_css: false,
              // Preserve formatting
              forced_root_block: '',
              force_br_newlines: false,
              force_p_newlines: false,
            }}
          />
        )}
      </div>
    </div>
  );
}
